import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon from 'dpl/common/components/Icon';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import ClampedMultiLineText from 'dpl/common/components/ClampedMultiLineText';

import { usePuppyData } from '../hooks/usePuppyData';
import Section from './Section';

function GetToKnowPuppyAttributes({ attributes, className, header, iconType }) {
  const [showFullList, setShowFullList] = useState(false);
  const shouldCollapseList = attributes.length > 5;

  return (
    <div className={classnames('GetToKnowPuppy__attributes', className)}>
      <h5 className="heading-5">{header}</h5>
      <ul
        className={classnames('mt2', {
          collapsed: shouldCollapseList && !showFullList
        })}
      >
        {attributes.map((attr, idx, arr) => (
          <li
            key={idx}
            className={classnames('flex pt4 bt b--black-10', {
              bn: idx === 0,
              pb4: idx < arr.length - 1,
              pb0: idx === arr.length - 1,
              'items-center': !attr.description
            })}
          >
            <Icon
              name={`${iconType}-${attr.icon}`}
              width="32px"
              height="32px"
            />
            <div className="ml2 ml4-md">
              <p className="fw-medium">{attr.title}</p>
              {attr.description && (
                <p className="secondary mt1">{attr.description}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={classnames('stone-700 underline', {
          dn: !shouldCollapseList || showFullList
        })}
        onClick={() => setShowFullList(prevState => !prevState)}
      >
        View more
      </button>
    </div>
  );
}

GetToKnowPuppyAttributes.propTypes = {
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  className: PropTypes.string,
  header: PropTypes.string.isRequired,
  iconType: PropTypes.string
};

GetToKnowPuppyAttributes.defaultProps = {
  className: '',
  iconType: 'fetch'
};

export default function GetToKnowPuppy() {
  const { puppy, breeder } = usePuppyData();

  return (
    <Section className="GetToKnowPuppy">
      <div className="row wider-gutters">
        <div className="col-md-6">
          <Section.Heading className="mb8 mb0-md">
            Get to know {puppy.name}
          </Section.Heading>
          {puppy.description && (
            <div className="bg-lightest-purple br4 pv4 ph4 pv6-md ph6-md mt15-md mb12 mb0-md">
              <ClampedMultiLineText
                fadeToBackgroundColor="#f4f4fe" // color-lightest-purple
                text={puppy.description}
                numOfLines={6}
                lineHeight={21.44}
                moreText="Read more"
                lessText="Show less"
                fadeToButton
                className="pre-line"
              />
              <div className="flex items-center mt2 mt4-md">
                <UserAvatar
                  size="sm"
                  user={{ ...breeder, id: breeder.user_id }}
                />
                <p className="secondary ml2">{breeder.display_name}</p>
              </div>
            </div>
          )}
        </div>
        <div className="col-md-6">
          <GetToKnowPuppyAttributes
            header="Health"
            attributes={puppy.health_attributes}
          />
          <GetToKnowPuppyAttributes
            header="Appearance"
            attributes={puppy.appearance_attributes}
            className="mt12 mt14-md"
          />
          {breeder.socialization_enrichment_programs.length > 0 && (
            <GetToKnowPuppyAttributes
              header="Socialization & Enrichment"
              attributes={breeder.socialization_enrichment_programs}
              iconType="socialization"
              className="mt12 mt14-md"
            />
          )}
        </div>
      </div>
    </Section>
  );
}
